import { useQuery } from "@tanstack/react-query";
import { apiCall } from "../lib/apiClient";
import type {
  PaymentDashboardSummaryResponse,
  PaymentIntentDetailResponse,
} from "../schemas/payment";

/**
 * UC-004 Payment tenant queries (Phase 19).
 *
 * - `usePaymentIntent(id)` — fetch detail s 30s polling (REQ-API-003 +
 *   REQ-BIZ-017). Polling se zastaví, jakmile `status !== "open"`.
 * - `useDashboardSummary()` — fetch Action Card stat ("Kolik dlužíš").
 *
 * RefetchInterval = 30000ms (30s) per requirement "30s refetch" v
 * PollingStatusBadge DoD.
 */

const REFETCH_MS = 30_000;

const KEY_INTENT = (id: string) => ["tenant", "payment-intent", id] as const;
const KEY_DASHBOARD = ["tenant", "payment", "dashboard"] as const;

export function usePaymentIntent(paymentId: string | undefined) {
  return useQuery({
    queryKey: KEY_INTENT(paymentId ?? "—"),
    queryFn: () =>
      apiCall<PaymentIntentDetailResponse>(
        `/api/tenant/payment-intents/${paymentId}`,
      ),
    enabled: typeof paymentId === "string" && paymentId.length > 0,
    // Stop polling once status finalized.
    refetchInterval: (query) => {
      const data = query.state.data as PaymentIntentDetailResponse | undefined;
      if (!data) return REFETCH_MS;
      if (data.status === "paid" || data.status === "expired") return false;
      return REFETCH_MS;
    },
  });
}

export function useDashboardSummary(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: KEY_DASHBOARD,
    queryFn: () =>
      apiCall<PaymentDashboardSummaryResponse>(
        "/api/tenant/payment-intents/summary",
      ),
    enabled: options?.enabled ?? true,
  });
}

export const PAYMENT_QUERY_KEYS = {
  intent: KEY_INTENT,
  dashboard: KEY_DASHBOARD,
  refetchMs: REFETCH_MS,
};
